import { ResolveFn } from '@angular/router';
import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Bundle, Patient, RelatedPerson } from 'fhir/r4';
import { ErrorService } from '../shared/error.service';
import { authBackendEndPointToken } from '../app.config';
import { AuthService } from '../shared/auth/auth.service';

export interface UserApproval {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  accountType: string;
  requestedRole: string;
  organizationId?: string;
  fhirResourceId?: string;
  status: string;
  submittedAt: string;
}

// ✅ Patient extended with resolved guardian RelatedPerson(s)
export interface PatientWithGuardians extends Patient {
  linkedGuardians?: RelatedPerson[];
}

export interface PatientRegistrationData {
  pending: UserApproval[];
  approved: PatientWithGuardians[];
  deceased: PatientWithGuardians[];
}

// Must match the extension URL used in FhirService.LinkGuardianAsync on the backend
const GUARDIAN_LINK_EXTENSION_URL = 'https://elikita.health/fhir/StructureDefinition/guardian-link';

/**
 * Splits a mixed Patient+RelatedPerson bundle apart, then attaches each
 * patient's linked guardian(s) by following the guardian-link extension.
 */
function splitAndAttachGuardians(bundle: Bundle | undefined): PatientWithGuardians[] {
  const entries = bundle?.entry || [];

  const patients = entries
    .map(e => e.resource)
    .filter((r): r is Patient => r?.resourceType === 'Patient');

  const relatedPersons = entries
    .map(e => e.resource)
    .filter((r): r is RelatedPerson => r?.resourceType === 'RelatedPerson');

  // ── DEBUG: remove once confirmed working ──────────────────────
  console.log('[Guardian Debug] Total entries:', entries.length);
  console.log('[Guardian Debug] Patients found:', patients.length);
  console.log('[Guardian Debug] RelatedPersons found:', relatedPersons.length);
  relatedPersons.forEach(rp => {
    console.log('[Guardian Debug] RelatedPerson:', rp.id, 'patient ref:', rp.patient?.reference);
  });
  patients.forEach(p => {
    const ext = p.extension?.find(e => e.url === GUARDIAN_LINK_EXTENSION_URL);
    console.log('[Guardian Debug] Patient:', p.id, 'guardian-link ext:', JSON.stringify(ext));
  });
  // ─────────────────────────────────────────────────────────────

  return patients.map(patient => {
    const linkExtension = patient.extension?.find(ext => ext.url === GUARDIAN_LINK_EXTENSION_URL);
    const relatedPersonRef = linkExtension?.extension
      ?.find(e => e.url === 'relatedPerson')
      ?.valueReference?.reference;

    console.log('[Guardian Debug] Patient', patient.id, '→ relatedPersonRef:', relatedPersonRef);

    const relatedPersonId = relatedPersonRef?.split('/').pop();
    console.log('[Guardian Debug] extracted relatedPersonId:', relatedPersonId);

    const linkedGuardians = relatedPersonId
      ? relatedPersons.filter(rp => rp.id === relatedPersonId)
      : [];

    console.log('[Guardian Debug] linkedGuardians matched:', linkedGuardians.length);

    return { ...patient, linkedGuardians };
  });
}

export const patientRegistrationCenterResolver: ResolveFn<PatientRegistrationData> = () => {
  const http = inject(HttpClient);
  const errorService = inject(ErrorService);
  const backendApi = inject(authBackendEndPointToken);
  const authService = inject(AuthService);

  const currentUser = authService.getCurrentUser();
  const organizationId = currentUser?.organizationId;

  if (!organizationId) {
    errorService.openandCloseError('No organization assigned to user');
    return of({ pending: [], approved: [], deceased: [] });
  }

  const pending$ = http.get<UserApproval[]>(
    `${backendApi}/api/admin/approvals/pending?accountType=Patient`
  ).pipe(
    catchError(err => {
      console.error('Failed to load pending approvals:', err);
      errorService.openandCloseError('Failed to load pending approvals');
      return of([]);
    })
  );

  // ✅ _revinclude pulls RelatedPerson resources that reference each returned Patient
  const approved$ = http.get<Bundle>(
    `${backendApi}/fhir/Patient?active=true&_count=1000&_revinclude=RelatedPerson:patient&_format=json`
  ).pipe(
    map(bundle => {
      const withGuardians = splitAndAttachGuardians(bundle);
      return withGuardians.filter(p =>
        p.active === true &&
        !(p as any).deceasedBoolean &&
        !(p as any).deceasedDateTime
      );
    }),
    catchError(err => {
      console.error('Failed to load approved patients:', err);
      errorService.openandCloseError('Failed to load approved patients');
      return of([]);
    })
  );

  const deceased$ = http.get<Bundle>(
    `${backendApi}/fhir/Patient?_sort=-_lastUpdated&_count=1000&_revinclude=RelatedPerson:patient&_format=json`
  ).pipe(
    map(bundle => {
      const withGuardians = splitAndAttachGuardians(bundle);
      return withGuardians.filter(p =>
        (p as any).deceasedBoolean === true ||
        !!(p as any).deceasedDateTime
      );
    }),
    catchError(err => {
      console.error('Failed to load deceased patients:', err);
      return of([]);
    })
  );

  return forkJoin({
    pending: pending$,
    approved: approved$,
    deceased: deceased$
  });
};